// OAuth 2.1 consent screen for the vibing.inc MCP server.
// Supabase redirects clients (Claude, ChatGPT, Cursor) here to approve access.

import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type AuthorizationDetails = {
  client?: { name?: string | null; client_uri?: string | null } | null;
  scope?: string | null;
  redirect_url?: string | null;
  redirect_to?: string | null;
};

type OAuthApi = {
  getAuthorizationDetails: (
    id: string,
  ) => Promise<{ data: AuthorizationDetails | null; error: { message: string } | null }>;
  approveAuthorization: (
    id: string,
  ) => Promise<{ data: AuthorizationDetails | null; error: { message: string } | null }>;
  denyAuthorization: (
    id: string,
  ) => Promise<{ data: AuthorizationDetails | null; error: { message: string } | null }>;
};

const oauthApi = () =>
  (supabase.auth as unknown as { oauth: OAuthApi }).oauth;

export const Route = createFileRoute("/.lovable/oauth/consent")({
  // Browser-only: the session lives in localStorage, absent during SSR.
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id:
      typeof s["authorization_id"] === "string" ? (s["authorization_id"] as string) : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      const next = location.pathname + location.searchStr;
      throw redirect({ to: "/dashboard", search: { redirect: next } });
    }
  },
  loader: async ({ location }) => {
    const authorizationId = new URLSearchParams(location.search).get("authorization_id")!;
    const { data, error } = await oauthApi().getAuthorizationDetails(authorizationId);
    if (error) throw new Error(error.message);
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  component: Consent,
  errorComponent: ({ error }) => (
    <main
      id="main"
      tabIndex={-1}
      className="min-h-dvh bg-background flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <h1 className="font-display text-4xl tracking-tight text-foreground leading-[1.05]">
          Authorization unavailable
        </h1>
        <p className="mt-4 text-muted-foreground">
          {String((error as Error)?.message ?? error)}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          Start the connection again from your MCP client.
        </p>
      </div>
    </main>
  ),
});

function Consent() {
  const details = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const clientName = details?.client?.name ?? "this app";

  async function decide(approve: boolean) {
    setBusy(approve ? "approve" : "deny");
    setError(null);
    const api = oauthApi();
    const { data, error: err } = approve
      ? await api.approveAuthorization(authorization_id)
      : await api.denyAuthorization(authorization_id);
    if (err) {
      setBusy(null);
      setError(err.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(null);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  return (
    <main
      id="main"
      tabIndex={-1}
      className="min-h-dvh bg-background flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
          the Vibing Company®
        </p>
        <h1 className="mt-6 font-display text-4xl tracking-tight text-foreground leading-[1.05]">
          Connect {clientName}
        </h1>
        <p className="mt-4 text-muted-foreground leading-relaxed">
          {clientName} is asking to use the vibing.inc library tools as you — reading the
          catalogue and, if your account is an admin, adding and maintaining gems.
        </p>

        {error && (
          <p role="alert" className="mt-6 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-10 flex gap-3">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => decide(true)}
            className="rounded-full bg-foreground px-6 py-3 text-sm font-medium text-background disabled:opacity-50"
          >
            {busy === "approve" ? "Connecting…" : "Approve"}
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => decide(false)}
            className="rounded-full border border-foreground/15 px-6 py-3 text-sm font-medium text-foreground disabled:opacity-50"
          >
            Deny
          </button>
        </div>
      </div>
    </main>
  );
}
